/* import __COLOCATED_TEMPLATE__ from './new-export-schedule-side-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import {
  type DatasetExportConfig,
  EVERY_DAY,
  type ExportRule,
  SchedulePreset,
} from 'embercom/models/reporting/dataset-export-schedule';
import type DatasetExportSchedule from 'embercom/models/reporting/dataset-export-schedule';
import { indexBy, isEmpty } from 'underscore';

interface Args {
  onClose: () => void;
  exportConfig: DatasetExportConfig;
  isOpen: boolean;
  timezone: string;
  isEditing?: boolean;
  selectedSchedule?: DatasetExportSchedule | null;
}

export default class NewExportScheduleSideDrawer extends Component<Args> {
  @service declare store: Store;
  @service declare intl: any;
  @tracked schedule = this.args.selectedSchedule
    ? this.args.selectedSchedule
    : this.makeSchedule('', this.presetExportRule(SchedulePreset.DAILY), this.args.exportConfig);

  makeSchedule(
    name: string,
    exportRule: ExportRule,
    exportConfig: DatasetExportConfig,
  ): DatasetExportSchedule {
    let schedule = this.store.createRecord('reporting/dataset-export-schedule');
    schedule.set('name', name);
    schedule.set('exportRule', exportRule);
    schedule.set('exportConfig', exportConfig);
    return schedule;
  }

  get disableSaveButton() {
    return isEmpty(this.schedule.name);
  }

  @action
  async scheduleExport() {
    await this.schedule.save();
    this.schedule = this.makeSchedule(
      '',
      this.presetExportRule(SchedulePreset.DAILY),
      this.args.exportConfig,
    );
    this.args.onClose();
  }

  get currentDayOfWeekLabel() {
    let weekDays = [
      this.intl.t('app.lib.reporting.week-days.sunday'),
      this.intl.t('app.lib.reporting.week-days.monday'),
      this.intl.t('app.lib.reporting.week-days.tuesday'),
      this.intl.t('app.lib.reporting.week-days.wednesday'),
      this.intl.t('app.lib.reporting.week-days.thursday'),
      this.intl.t('app.lib.reporting.week-days.friday'),
      this.intl.t('app.lib.reporting.week-days.saturday'),
    ];
    return weekDays[this.currentDayOfWeek];
  }

  get currentDayOfWeek() {
    return new Date().getDay();
  }

  presetExportRule(preset: SchedulePreset): ExportRule {
    switch (preset) {
      case SchedulePreset.DAILY:
        return {
          daysOfWeek: EVERY_DAY,
          deliveryTime: { hour: 12, minute: 0, timezone: this.args.timezone },
          interval: { step: 'weekly', value: 1 },
        };
      case SchedulePreset.WEEKLY:
        return {
          daysOfWeek: [this.currentDayOfWeek],
          deliveryTime: { hour: 12, minute: 0, timezone: this.args.timezone },
          interval: { step: 'weekly', value: 1 },
        };
      case SchedulePreset.MONTHLY:
        return {
          daysOfWeek: [this.currentDayOfWeek],
          deliveryTime: { hour: 12, minute: 0, timezone: this.args.timezone },
          interval: { step: 'monthly', value: 1, dayPosition: 'last' },
        };
      default:
        throw new Error(`Unknown preset: ${preset}`);
    }
  }

  @action
  updateSelection(preset: SchedulePreset) {
    this.schedule.updateExportRule(this.presetExportRule(preset));
  }

  get selectedPresetLabel() {
    let step = this.schedule.exportRule.interval.step;
    let daysOfWeek = this.schedule.exportRule.daysOfWeek;
    let indexedFrequencyItems = indexBy(this.frequencyItems, 'value');

    if (step === SchedulePreset.WEEKLY && daysOfWeek.length === 7) {
      return indexedFrequencyItems[SchedulePreset.DAILY].text;
    }
    return indexedFrequencyItems[step].text;
  }

  get frequencyItems() {
    return [
      {
        text: this.intl.t(
          'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.daily',
        ),
        value: SchedulePreset.DAILY,
        onSelectItem: () => {
          this.updateSelection(SchedulePreset.DAILY);
        },
      },
      {
        text: this.intl.t(
          'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.weekly',
          {
            dayOfWeek: this.currentDayOfWeekLabel,
          },
        ),
        value: SchedulePreset.WEEKLY,
        onSelectItem: () => {
          this.updateSelection(SchedulePreset.WEEKLY);
        },
      },
      {
        text: this.intl.t(
          'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.monthly',
          {
            dayOfWeek: this.currentDayOfWeekLabel,
          },
        ),
        value: SchedulePreset.MONTHLY,
        onSelectItem: () => {
          this.updateSelection(SchedulePreset.MONTHLY);
        },
      },
    ];
  }

  @action
  onClose() {
    this.args.onClose();
    this.schedule.deleteRecord();
    this.schedule.unloadRecord();
    this.schedule = this.makeSchedule(
      '',
      this.presetExportRule(SchedulePreset.DAILY),
      this.args.exportConfig,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::NewExportScheduleSideDrawer': typeof NewExportScheduleSideDrawer;
    'reporting/new-export-schedule-side-drawer': typeof NewExportScheduleSideDrawer;
  }
}
